import { reactive, ref } from 'vue'
import { isInf } from '../is'
import { lerp, smoothstep } from '../math'
import type { Vector } from '../math/vector'

export type InterpolationConfig = {
  duration: number
  initial?: number
  reverseBack?: boolean
  frameCallback?: (value: number, time: number, delta: number) => void
  onFinish?: () => void
}

export type InterpolationState = { value: number; numRuns: number; running: boolean; stopped: boolean; }

export type InterpolationRunner = {
  run: (vFrom: number, towards: number, curve?: Vector | null, callback?: (value: number) => void, duration?: number) => Promise<void>
  runTo: (towards: number, curve?: Vector, callback?: (value: number) => void, duration?: number) => Promise<void>
  cancel: () => void
  reset: () => void;
  state: InterpolationState
}

export const useInterpolation = (cfg: InterpolationConfig): InterpolationRunner => {
  if (cfg.duration <= 0) {
    throw new Error(`The specified duration is <= 0`)
  }

  const state = reactive<InterpolationState>({
    value: cfg.initial || 0,
    numRuns: 0,
    running: false,
    stopped: false
  })
  const timer = ref<number | undefined>(undefined);

  const reset = () => {
    state.stopped = true;
    if (typeof timer.value !== 'undefined') {
      cancelAnimationFrame(timer.value)
    }
  }

  const cancelTimer = () => {
    if (typeof timer.value === 'undefined') {
      return;
    }

    cancelAnimationFrame(timer.value)
    timer.value = undefined
    state.running = false;
  }

  const setTimer = (duration: number, fun: (time: number, delta: number) => void) => {
    state.stopped = false;
    state.running = true;

    if (typeof timer.value !== 'undefined') {
      cancelTimer()
    }

    return new Promise((resolve) => {
      let timeStarted: number | undefined = undefined;
      let lastTime: number = performance.now();

      const loop = (now: number) => {
        state.running = true;
        const delta = now - lastTime;
        lastTime = now;
        if (timeStarted === undefined) timeStarted = now
        const elapsed = (now - timeStarted) / 1000.0;

        fun(elapsed, delta / 1000.0);

        if ((elapsed >= (duration-0.00001)) || state.stopped) {
          state.stopped = false;
          cancelTimer()
          resolve(now)

          if (cfg.onFinish) {
            cfg.onFinish()
          }
          return
        }

        timer.value = requestAnimationFrame(loop)
      }

      timer.value = requestAnimationFrame(loop)
    })
  }

  const run = async (vFrom: number, towards: number, curve?: Vector | null, callback?: (value: number) => void, duration?: number) => {
    state.numRuns += 1
    state.value = vFrom
    const dur = duration || cfg.duration

    await setTimer(dur, (time: number, delta: number) => {
      if (state.stopped) return;

      const tn = time / dur

      const A = curve ? curve.x : 0.0
      const B = curve ? curve.y : 1.0
      const scale = curve === null ? tn : smoothstep(A, B, tn)
      const half = dur * 0.5
      const distHalf = (time - half) / dur

      if (isNaN(scale) || isInf(scale)) {
        throw new Error(`scale ${scale} is inappropriate.`)
      }

      if (cfg.frameCallback) {
        cfg.frameCallback(state.value, time, delta)
      }



      let goal = towards

      if (cfg.reverseBack) {
        goal = lerp(goal, vFrom, smoothstep(A * 0.5, B * 0.5, distHalf))
      }

      const nextValue = lerp(state.value, goal, scale)

      if (isNaN(nextValue) || isInf(nextValue)) {
        throw new Error(`The interpolated value ${nextValue} is inappropriate.`)
      }

      if (callback) {
        callback(nextValue);
      }
      state.value = nextValue
    })

    state.value = towards;
  }

  const runTo = async (towards: number, curve?: Vector | null, callback?: (value: number) => void, duration?: number) => {
    return run(state.value, towards, curve, callback, duration)
  }

  return { state, run, runTo, cancel: () => cancelTimer(), reset }
}
